import { get, merge, set } from 'lodash';
import { APIS, METHOD_GET, METHOD_POST } from 'src/lib/constants/apis';
import { PERSON_ROLLE } from 'src/lib/constants/constant';
import { STRAPI_URL } from 'src/lib/constants/url';
import { getFieldNamefromDataPath } from 'src/utils/helper';
import { getQueryString } from './Population';

// import schemas
import PersonSchema from 'src/lib/paths/person_FULL_Paths.json';
const PP = PersonSchema.attributes;

/**
 * Authenticates the user.
 * The server sets an HttpOnly accessToken cookie on successful login.
 * @param {string} userName | username entered by user
 * @param {string} password | password entered by user
 * @returns | returns a responseBody from the API
 */
export default async function Login(userName: string, password: string) {
	let response = await fetch(`${STRAPI_URL}/api/auth/local`, {
		method: METHOD_POST,
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ identifier: userName, password: password }),
	});

	if (response.ok) {
		let responseData = await response.json();
		return responseData;
	}

	const err = await response.text();
	throw new Error(err);
}

/**
 * Logs out the user by calling the server logout endpoint
 * which clears the HttpOnly accessToken cookie.
 */
export async function Logout() {
	try {
		let response = await fetch(`${STRAPI_URL}/api/auth/logout`, {
			method: METHOD_POST,
			credentials: 'include',
		});
		if (!response.ok) {
			throw new Error('NETWORK_NOT_OK');
		}
	} catch (err) {
		console.error('ERROR logging out: ', err);
	}
	// Always reload after logout to clear all React state
	window.location.reload();
}

/**
 * Fetches the current user's data.
 * The accessToken HttpOnly cookie is sent automatically via credentials: 'include'.
 * No jwt parameter is needed — the cookie handles authentication.
 */
export async function GetUserMe() {
	let poplevel = [{ Person: ['id', 'Portal_Benutzerdaten_Portal_Rolle'], 'Person.Organisation': 'Geschaeftsbezogene_Daten_Name_Der_Organisation' }];
	let filter: any[] = [];
	let sort: any[] = [];
	let pagination: object = {};
	let fields: any = [];
	let response = await fetch(`${STRAPI_URL}/api/users/me${getQueryString(poplevel, filter, sort, pagination, fields)}`, {
		method: METHOD_GET,
		credentials: 'include',
	});

	if (!response.ok) {
		throw new Error(await response.text());
	}
	const responseData = await response.json();
	return responseData;
}

export async function ChangeUserPassword(data: Record<string, any>) {
	let response = await fetch(`${STRAPI_URL}/api/auth/change-password`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	});

	if (response.ok) {
		let responseData = await response.json();
		return responseData;
	}

	const err = await response.text();
	throw new Error(err);
}

export async function CheckIfUserAndOrganisationExist(organisationData: any = {}, loginDaten?: any, personDaten?: any) {
	let bodyData: any = {
		data: {
			organisation: get(organisationData, 'attributes', {}),
			performRegister: false,
		},
	};
	if (loginDaten) {
		merge(bodyData.data, loginDaten);
	}
	if (personDaten) {
		set(bodyData, 'data.person', get(personDaten, 'attributes'));
	}

	let response: any = await fetch(`${STRAPI_URL}/api/auth/local/register`, {
		method: METHOD_POST,
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(bodyData.data),
	});
	if (!response.ok) {
		throw new Error(response);
	}
	let responseData = await response.json();
	return responseData;
}

export async function CheckIfPersonExist(personEmail: string) {
	const filter = {
		$and: [
			{ [getFieldNamefromDataPath(PP.Kontaktdaten_E_Mail.path)]: { $eq: personEmail } },
			{ [getFieldNamefromDataPath(PP.Personenbezogene_Daten_Rolle.path)]: { $eq: PERSON_ROLLE.AnsprechpartnerAntragsteller } },
		],
	};

	let response = await fetch(`${STRAPI_URL}/api/${APIS.Person}${getQueryString([], filter)}`, {
		method: METHOD_GET,
		credentials: 'include',
	});

	if (!response.ok) throw new Error(await response.text());
	const responseData = await response.json();
	return responseData;
}

export async function RegisterUserAndOrganisation(
	organisationData: any,
	loginDetails: any = {},
	personData: any,
	registerUserOnly: boolean = false,
	personId: number = 0,
	organisationId: number = 0,
) {
	let data = {
		...loginDetails,
		blocked: false,
		confirmed: false,
		person: get(personData, 'attributes', {}),
		organisation: get(organisationData, 'attributes', {}),
		performRegister: true,
		registerUserOnly: registerUserOnly,
	};

	if (personId) {
		set(data, 'person', { id: personId });
	}
	if (organisationId) {
		set(data, 'ZuwendungsempfaengerId', organisationId);
	}
	let response = await fetch(`${STRAPI_URL}/api/auth/local/register`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	});

	if (response.ok) {
		let responseData = await response.json();
		return responseData;
	}

	const err = await response.text();
	throw new Error(err);
}

export async function ConfirmUserEmail(confirmationToken: string) {
	let response = await fetch(`${STRAPI_URL}/api/auth/email-confirmation?confirmation=${confirmationToken}`, {
		method: METHOD_GET,
	});
	return response;
}

export async function ResetPassword(data: any) {
	let response = await fetch(`${STRAPI_URL}/api/auth/reset-password`, {
		method: METHOD_POST,
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	});

	if (response.ok) {
		let responseData = await response.json();
		return responseData;
	}

	const err = await response.text();
	throw new Error(err);
}

export async function SendForgotPasswordMail(data: any) {
	let response = await fetch(`${STRAPI_URL}/api/auth/forgot-password`, {
		method: METHOD_POST,
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	});

	if (response.ok) {
		let responseData = await response.json();
		return responseData;
	}

	const err = await response.text();
	throw new Error(err);
}
